import clsx from 'clsx';
import HubertCombobox from 'components/HubertUI/HubertCombobox';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'rtk/query/hooks';
import { selectQuestionLanguage } from 'rtk/questionEditorSelector';
import type { EEvaluationType, TMatrixEvaluationConditional } from 'types/evaluation.types';
import type { TSelectBase } from 'types/general.types';
import type { TFormMatrix } from '@hubertai/screening-question-types';
import EvaluationPopover from '..';

interface Props extends ComponentProps<'div'> {
  form: TFormMatrix;
  item: TMatrixEvaluationConditional;
  onUpdateItem: (updateField: string, value: string | string[]) => void;
  children: ReactNode;
}

const MatrixEvaluationConditionalItemv2 = ({ form, item, onUpdateItem, children, ...props }: Props) => {
  const { t } = useTranslation('Templates');
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));

  const sliderEvaluationOperatorList = useMemo(
    () => [
      { label: t('evaluationConditional.operators.lessThan'), value: 'LESS_THAN' },
      { label: t('evaluationConditional.operators.lessThanOrEqualTo'), value: 'LESS_THAN_EQUAL_TO' },
      { label: t('evaluationConditional.operators.equals'), value: 'EQUALS' },
      { label: t('evaluationConditional.operators.greaterThanOrEqualTo'), value: 'GREATER_THAN_EQUAL_TO' },
      { label: t('evaluationConditional.operators.greaterThan'), value: 'GREATER_THAN' },
      { label: t('evaluationConditional.operators.between'), value: 'BETWEEN' },
    ],
    [t],
  );
  const [operator, setOperator] = useState<TSelectBase>(
    // @ts-ignore
    sliderEvaluationOperatorList.find((x) => x.value === item.condition.operator),
  );

  useEffect(() => {
    setOperator(
      // @ts-ignore
      sliderEvaluationOperatorList.find((x) => x.value === item.condition.operator),
    );
  }, [item.condition.operator]);

  const [evaluation, setEvaluation] = useState<{
    name: EEvaluationType;
    bonus_points?: number;
  }>({ name: item.name, bonus_points: item.bonus_points });

  return (
    <div
      {...props}
      className={clsx('flex flex-col justify-between gap-4', 'my-2', 'rounded-md border', 'hubert-vertical-center')}
    >
      {children}

      <div className={clsx('flex flex-row justify-between gap-4', 'p-2', 'w-full', 'hubert-vertical-center')}>
        <div className={clsx('flex flex-row justify-start', 'w-[30%]', 'hubert-vertical-center')}>
          <HubertCombobox
            isMulti={false}
            itemName={t('evaluationConditional.operatorItemName')}
            items={sliderEvaluationOperatorList}
            className={clsx('w-full', 'text-xs')}
            value={operator.value}
            onValueChange={(value) => {
              if (onUpdateItem) {
                onUpdateItem('condition-operator', value);
              }
            }}
          />
        </div>

        <div className={clsx('flex flex-row justify-end gap-2', 'w-[70%]', 'hubert-vertical-center')}>
          <div>
            {(() => {
              if (!item || !item.condition || !item.condition.value) {
                return <div>{t('evaluationConditional.erroneousConditionValue')}</div>;
              }
              const val = item.condition.value;

              if (typeof val === 'string') {
                return val;
              } else if (typeof val === 'number') {
                return val;
              } else if (typeof val === 'object') {
                if ('column_id' in val) {
                  return (
                    <HubertCombobox
                      isMulti={false}
                      itemName={t('evaluationConditional.columnItemName')}
                      items={form.columns.map((x) => {
                        return {
                          label: x.label[questionLanguage]!,
                          value: x.id,
                        };
                      })}
                      value={val.column_id}
                      onValueChange={(value) => {
                        if (onUpdateItem) {
                          onUpdateItem('condition-operator', value);
                        }
                      }}
                    />
                  );
                }
              } else if (Array.isArray(item.condition.value) && item.condition.value.length) {
                console.log('item.condition.value isArray', item.condition.value);
                return item.condition.value[0];
              } else {
                return <div>{t('evaluationConditional.erroneousConditionValue2')}</div>;
              }
            })()}
          </div>

          <div>
            {(() => {
              if (Array.isArray(item.condition.value) && item.condition.value.length === 2) {
                return item.condition.value[1];
              } else {
                return null;
              }
            })()}
          </div>
        </div>
      </div>

      <div className={clsx('flex flex-row justify-center gap-4', 'p-2', 'w-full', 'hubert-vertical-center')}>
        <div>{t('evaluationConditional.thenEvaluateAs')}</div>

        {/* @ts-ignore */}
        <EvaluationPopover
          allowedTabs={['info', 'warning', 'bonus', 'disqualified']}
          nodeId=""
          customKey=""
          evaluation={evaluation}
          onEvaluationChange={(e: any) => {
            setEvaluation(e);
            if (onUpdateItem) {
              onUpdateItem('evaluation', e);
            }
          }}
        />
      </div>
    </div>
  );
};

export default MatrixEvaluationConditionalItemv2;
